import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { environment } from '../../../../environments/environment';

declare global {
  interface Window {
    grecaptcha?: {
      ready(callback: () => void): void;
      execute(siteKey: string, options: { action: string }): Promise<string>;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class RecaptchaService {
  private scriptLoaded = false;
  private loadingPromise: Promise<void> | null = null;
  private readyPromise: Promise<void> | null = null;
  private readonly siteKey = environment.recaptcha?.siteKey ?? '';

  private readonly document = inject(DOCUMENT);

  private loadScript(): Promise<void> {
    if (this.scriptLoaded || !this.siteKey) {
      return Promise.resolve();
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = new Promise<void>((resolve, reject) => {
      if (typeof window === 'undefined') {
        resolve();
        return;
      }

      const existing = this.document.querySelector<HTMLScriptElement>(
        'script[src^="https://www.google.com/recaptcha/api.js"]',
      );
      if (existing) {
        if (window.grecaptcha?.execute) {
          this.scriptLoaded = true;
          resolve();
          return;
        }

        const onLoad = () => {
          this.scriptLoaded = true;
          cleanup();
          resolve();
        };
        const onError = () => {
          this.loadingPromise = null;
          cleanup();
          reject(new Error('Failed to load reCAPTCHA script'));
        };
        const cleanup = () => {
          existing.removeEventListener('load', onLoad);
          existing.removeEventListener('error', onError);
        };

        existing.addEventListener('load', onLoad, { once: true });
        existing.addEventListener('error', onError, { once: true });
        return;
      }

      const script = this.document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        this.loadingPromise = null;
        reject(new Error('Failed to load reCAPTCHA script'));
      };

      this.document.head.appendChild(script);
    });

    return this.loadingPromise;
  }

  private ensureReady(): Promise<void> {
    if (!this.siteKey || typeof window === 'undefined') {
      return Promise.resolve();
    }

    if (window.grecaptcha?.execute) {
      return Promise.resolve();
    }

    if (this.readyPromise) {
      return this.readyPromise;
    }

    this.readyPromise = (async () => {
      await this.loadScript();

      await new Promise<void>((resolve) => {
        const waitUntilReady = () => {
          if (!window.grecaptcha?.ready) {
            setTimeout(waitUntilReady, 25);
            return;
          }

          window.grecaptcha.ready(() => resolve());
        };

        waitUntilReady();
      });
    })()
      .catch((error) => {
        this.readyPromise = null;
        throw error;
      })
      .then(() => {
        this.readyPromise = null;
      });

    return this.readyPromise;
  }

  async preload(): Promise<void> {
    try {
      await this.ensureReady();
    } catch {
      // Swallow errors so app bootstrap is not blocked by reCAPTCHA outages.
    }
  }

  async execute(action: string): Promise<string> {
    if (!this.siteKey) {
      return '';
    }
    if (typeof window === 'undefined') {
      return '';
    }

    try {
      await this.ensureReady();
    } catch {
      return '';
    }

    if (!window.grecaptcha || !window.grecaptcha.execute) {
      return '';
    }

    return new Promise<string>((resolve) => {
      window.grecaptcha!.ready(async () => {
        try {
          const token = await window.grecaptcha!.execute(this.siteKey, { action });
          resolve(token);
        } catch {
          resolve('');
        }
      });
    });
  }
}
